import { UserActivator } from '@/app/platform/platform-core/user/activators/types';
import { Inject, Injectable } from '@angular/core';
import {
  AUTHENTICATION_REFRESHER,
  AuthenticationRefresher,
  ValidAuthentication,
  ValidAuthenticationResponse
} from '@/app/platform/platform-core/user/services/types';
import { UserStore } from '@/app/platform/platform-core/user/store/user.store';
import { Router } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import {
  ACTIVE_APP_QUERIES,
  ActiveAppQueries
} from '@/app/platform/platform-core/user/queries/types';
import { firstRouteQueryParam, postLoginRedirectStorageKey } from '@/app/platform/platform-core/user/constants';
import { MatDialog } from '@angular/material/dialog';
import { currentTab } from '@/app/current-tab';
import { ValidResult } from '@common/result/Result';
import { AsyncResult } from '@common/result/asyncResult';

@Injectable({
  providedIn: 'root'
})
export class StoreUserActivator implements UserActivator {
  private subscription?: Subscription;
  private pendingRenewal?: Promise<void>;

  constructor(
    private readonly userStore: UserStore,
    private readonly router: Router,
    @Inject(ACTIVE_APP_QUERIES)
    private readonly activeAppQueries: ActiveAppQueries,
    private readonly matDialog: MatDialog,
    @Inject(AUTHENTICATION_REFRESHER)
    private readonly authenticationRefresher: AuthenticationRefresher
  ) {}

  activateOnDesktop(
    { authentication, refresher$ }: ValidAuthenticationResponse,
    doRedirect = false
  ): void {
    this.userStore.setAuthenticatedUser(authentication);
    this.subscribeToAuthenticationRefresher(refresher$);

    if (doRedirect) {
      this.navigateForFirstTime();
    }
  }

  deactivate(redirectToLogin = true): void {
    this.userStore.clearAuthenticatedUser();
    this.subscription?.unsubscribe();
    this.matDialog.closeAll();
    if (redirectToLogin) {
      this.router.navigate(['/login']);
    }
    currentTab.clearAuth();
  }

  /**
   * Vernieuwt nu i.p.v. bij de volgende refresh-tick. Een bevroren tab (iOS Safari op de
   * achtergrond, laptop in slaap) mist die ticks; zijn token verloopt intussen en verzoeken
   * die bij het terugkeren meteen vertrekken (visibilitychange) zou de server weigeren.
   * Gelijktijdige aanvragen delen één vernieuwing; de refresher deelt die op zijn beurt met
   * een tick die op hetzelfde moment loopt.
   */
  renewAuthentication(): Promise<void> {
    if (!this.pendingRenewal) {
      this.pendingRenewal = this.renewAuthenticationOnce().finally(() => (this.pendingRenewal = undefined));
    }
    return this.pendingRenewal;
  }

  private async renewAuthenticationOnce(): Promise<void> {
    let result: Awaited<AsyncResult<ValidAuthentication>>;
    try {
      result = await this.authenticationRefresher.refresh();
    } catch {
      // Transiënt (netwerk/5xx): sessie behouden, de volgende tick probeert opnieuw.
      return;
    }
    if (!this.userStore.getValue().authenticatedUser) {
      return;
    }
    if (result instanceof ValidResult) {
      this.userStore.setAuthenticatedUser(result.value);
      return;
    }
    this.deactivate();
  }

  private subscribeToAuthenticationRefresher(refresher: Observable<ValidAuthentication>): void {
    this.subscription = refresher.subscribe(
      v => {
        const { authenticatedUser } = this.userStore.getValue();
        if (authenticatedUser) {
          this.userStore.setAuthenticatedUser(v);
        }
      },
      e => {
        this.deactivate();
      }
    );
  }

  private navigateForFirstTime(): void {
    // Terugkeer na login (spec 2026-10-20-chart-share §4.3): een publieke pagina
    // (deellink) zet vóór het inloggen haar eigen route klaar.
    const redirect = this.consumePostLoginRedirect();
    if (redirect) {
      void this.router.navigateByUrl(redirect);
      return;
    }
    this.router.navigate(['/'], {
      queryParams: {
        [firstRouteQueryParam]: true
      }
    });
  }

  /** Alleen app-interne paden (leidende '/', geen '//'): nooit naar een extern adres springen. */
  private consumePostLoginRedirect(): string | null {
    try {
      const value = sessionStorage.getItem(postLoginRedirectStorageKey);
      sessionStorage.removeItem(postLoginRedirectStorageKey);
      return value && value.startsWith('/') && !value.startsWith('//') ? value : null;
    } catch {
      return null;
    }
  }
}
